import { useState } from "react";

import { ActionIcon, Box, Flex, Stack, Textarea } from "@mantine/core";
import { IconPlus, IconSend } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Text } from "components";
import { BankCreditNoteResponse } from "types";

const TITLE_COLORS = {
  BANK: "rgba(176, 70, 172, 1)",
  PROCESSING: "rgba(70, 176, 176, 1)",
};

interface NotesSectionProps {
  type: "BANK" | "PROCESSING";
  notes?: BankCreditNoteResponse[];
  onAddNote?: (note: string) => void;
}

export const NotesSection = ({ type, notes = [], onAddNote }: NotesSectionProps) => {
  const { t } = useTranslation();
  const [isAdding, setIsAdding] = useState(false);
  const [newNote, setNewNote] = useState("");

  const titleKey = type === "BANK" 
    ? "bankSelection.notes.forBank" 
    : "bankSelection.notes.forProcessing";

  const handleAddNote = () => {
    if (newNote.trim() && onAddNote) {
      onAddNote(newNote.trim());
      setNewNote("");
      setIsAdding(false);
    }
  };

  return (
    <Box
      p="md"
      style={{
        border: "1px solid rgba(50, 51, 67, 0.15)",
        borderRadius: 12,
        height: "100%",
        backgroundColor: "rgba(241, 242, 244, 1)",
      }}
    >
      <Flex justify="space-between" align="center" mb="md">
        <Text fw={600} c={TITLE_COLORS[type]}>
          {t(titleKey)}
        </Text>
        {onAddNote && !isAdding && (
          <ActionIcon
            variant="filled"
            radius="xl"
            size="sm"
            color={TITLE_COLORS[type]}
            onClick={() => setIsAdding(true)}
          >
            <IconPlus size={14} color="white" />
          </ActionIcon>
        )}
      </Flex>

      <Stack gap="md">
        {isAdding && (
          <Box>
            <Textarea
              placeholder={t("bankSelection.notes.placeholder")}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              minRows={2}
              maxRows={4}
              autosize
              styles={{
                input: {
                  border: "1px solid rgba(50, 51, 67, 0.15)",
                  borderRadius: 8,
                  backgroundColor: "white",
                },
              }}
            />
            <Flex justify="flex-end" mt="xs" gap="sm" align="center">
              <Text
                size="xs"
                c="gray"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  setIsAdding(false);
                  setNewNote("");
                }}
              >
                {t("action.cancel")}
              </Text>
              <ActionIcon
                variant="filled"
                radius="xl"
                color={TITLE_COLORS[type]}
                onClick={handleAddNote}
                disabled={!newNote.trim()}
              >
                <IconSend size={14} />
              </ActionIcon>
            </Flex>
          </Box>
        )}

        {notes.length > 0 ? (
          notes.map((note) => (
            <Box key={note.id}>
              <Text size="sm" fw={600} mb={4}>
                {`${note.info?.createdBy?.fullName}:`}
              </Text>
              <Text size="sm" c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
                {note.note}
              </Text>
            </Box>
          ))
        ) : (
          !isAdding && (
            <Text size="sm" c="dimmed">
              {t("bankSelection.notes.empty")}
            </Text>
          )
        )}
      </Stack>
    </Box>
  );
};
